import React, {
  useState,
  useEffect,
  useRef,
  lazy,
  Suspense,
  useCallback,
} from "react";
import AppHeader from "../../pages/LandingPage/AppHeader/AppHeader";
import Spinner from "../../components/common/Spinner.jsx";
import BJSMapSection from "./BJSMapSection/BJSMapSection";
import CTASection from "./CTASection/CTASection";
import FooterForLandingPage from "../LandingPage/LandingPageFooter/FooterForLandingPage";
import BJSPresence from "./BJSPresence/BJSPresence.jsx";
import ChatbotWidget from "./ChatbotWidget";
import axios from "axios";
import { message } from "antd";

const ProgramsSection = lazy(() => import("./ProgramsSection/ProgramsSection"));
const FlipBookSection = lazy(() => import("./FlipBookSection/FlipBookSection"));
const NetworkCarousel = lazy(() => import("./NetworkCarousel/NetworkCarousel"));
const ImpactSection = lazy(() => import("./ImpactSection/ImpactSection"));
const FromTheFounderSection = lazy(
  () => import("./FromTheFounderSection/FromTheFounderSection"),
);
const KaryaKartaSection = lazy(
  () => import("./KaryaKartaSection/KaryaKartaSection"),
);

const BASE_URL = process.env.REACT_APP_BASE_URL;
const LANDING_CACHE_TTL_MS = 5 * 60 * 1000;
const LANDING_STATS_CACHE_KEY = "landing:stats:v1";
const LANDING_PROGRAMS_CACHE_KEY = "landing:programs:v1";

const readLandingCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > LANDING_CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed?.data ?? null;
  } catch (_err) {
    return null;
  }
};

const writeLandingCache = (key, data) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch (_err) { }
};

const LandingPage = () => {
  const [stats, setStats] = useState({});
  const [programs, setPrograms] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [statsFetchTimeMs, setStatsFetchTimeMs] = useState(null);
  const [, setProgramsFetchTimeMs] = useState(null);
  const [topReadyTimeMs, setTopReadyTimeMs] = useState(null);
  const [visibleSections, setVisibleSections] = useState({
    programs: false,
    impact: false,
    flipBook: false,
    networkCarousel: false,
    founder: false,
    karyaKarta: false,
  });
  const startTimeRef = useRef(performance.now());
  const programsRef = useRef(null);
  const impactRef = useRef(null);
  const flipBookRef = useRef(null);
  const networkCarouselRef = useRef(null);
  const founderRef = useRef(null);
  const karyaKartaRef = useRef(null);
  const idleCallbackRef = useRef(null);
  const deferredPrefetchRequestedRef = useRef(false);
  const [metricsUnlocked, setMetricsUnlocked] = useState(false);
  const clickCountRef = useRef(0);
  const clickWindowStartRef = useRef(0);

  useEffect(() => {
    startTimeRef.current = performance.now();
    fetchLandingPageStats();
  }, []);

  const handleSecretClick = () => {
    const now = Date.now();
    if (now - clickWindowStartRef.current > 3000) {
      clickWindowStartRef.current = now;
      clickCountRef.current = 0;
    }

    clickCountRef.current += 1;
    if (clickCountRef.current >= 5) {
      const newVal = !metricsUnlocked;
      setMetricsUnlocked(newVal);
      clickCountRef.current = 0;
      if (newVal) {
        message.info("Dev metrics visible");
      } else {
        message.info("Dev metrics hidden");
      }
    }
  };

  const fetchProminentPrograms = useCallback(async () => {
    if (programs.length > 0 || loadingPrograms) {
      return;
    }

    const cachedPrograms = readLandingCache(LANDING_PROGRAMS_CACHE_KEY);
    if (cachedPrograms?.length) {
      setPrograms(cachedPrograms);
      setProgramsFetchTimeMs(0);
      return;
    }

    setLoadingPrograms(true);
    try {
      const apiStartTime = performance.now();
      const response = await axios.get(
        `${BASE_URL}/landingPageProminentPrograms`,
        {
          headers: {
            "Cache-Control": "public, max-age=300, stale-while-revalidate=120",
          },
        },
      );
      const fetchDuration = Math.round(performance.now() - apiStartTime);
      setProgramsFetchTimeMs(fetchDuration);

      console.info(
        `[LandingPage] landingPageProminentPrograms fetched in ${fetchDuration} ms`,
        {
          url: `${BASE_URL}/landingPageProminentPrograms`,
          status: response.status,
          headers: response.headers,
          payloadSize: response?.data
            ? JSON.stringify(response.data).length
            : 0,
        },
      );

      const fetchedPrograms = response?.data?.data?.prominentPrograms || [];
      setPrograms(fetchedPrograms);
      writeLandingCache(LANDING_PROGRAMS_CACHE_KEY, fetchedPrograms);
    } catch (err) {
      console.error(
        "[LandingPage] Error occurred while fetching landingPageProminentPrograms",
        err,
      );
      message.error("Error occurred while fetching the prominent programs");
    } finally {
      setLoadingPrograms(false);
    }
  }, [loadingPrograms, programs.length]);

  useEffect(() => {
    if (statsFetchTimeMs === null || deferredPrefetchRequestedRef.current) {
      return;
    }

    deferredPrefetchRequestedRef.current = true;

    const preloadDeferredSections = async () => {
      await Promise.all([
        import("./ProgramsSection/ProgramsSection"),
        import("./ImpactSection/ImpactSection"),
        import("./FlipBookSection/FlipBookSection"),
        import("./NetworkCarousel/NetworkCarousel"),
        import("./FromTheFounderSection/FromTheFounderSection"),
        import("./KaryaKartaSection/KaryaKartaSection"),
      ]);
      fetchProminentPrograms();
    };

    const schedule = () => {
      if (typeof window.requestIdleCallback === "function") {
        idleCallbackRef.current = window.requestIdleCallback(
          preloadDeferredSections,
          {
            timeout: 500,
          },
        );
      } else {
        idleCallbackRef.current = window.setTimeout(
          preloadDeferredSections,
          300,
        );
      }
    };

    schedule();

    return () => {
      if (idleCallbackRef.current !== null) {
        if (typeof window.cancelIdleCallback === "function") {
          window.cancelIdleCallback(idleCallbackRef.current);
        } else {
          window.clearTimeout(idleCallbackRef.current);
        }
      }
    };
  }, [statsFetchTimeMs, fetchProminentPrograms]);

  useEffect(() => {
    if (statsFetchTimeMs === null) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      setTopReadyTimeMs(Math.round(performance.now() - startTimeRef.current));
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [statsFetchTimeMs]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const sectionName = entry.target.dataset.section;
          if (!sectionName) {
            return;
          }

          setVisibleSections((prev) => {
            if (prev[sectionName]) {
              return prev;
            }
            return { ...prev, [sectionName]: true };
          });
        });
      },
      {
        rootMargin: "200px 0px 200px 0px",
        threshold: 0.1,
      },
    );

    const refs = [
      programsRef,
      impactRef,
      flipBookRef,
      networkCarouselRef,
      founderRef,
      karyaKartaRef,
    ];

    refs.forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (visibleSections.programs) {
      fetchProminentPrograms();
    }
  }, [visibleSections.programs, fetchProminentPrograms]);

  async function fetchLandingPageStats() {
    const apiStartTime = performance.now();
    const cachedStats = readLandingCache(LANDING_STATS_CACHE_KEY);
    if (cachedStats && Object.keys(cachedStats).length > 0) {
      setStats(cachedStats);
      setStatsFetchTimeMs(0);
      return;
    }

    setLoadingStats(true);

    try {
      const response = await axios.get(`${BASE_URL}/landingPageStats`, {
        headers: {
          "Cache-Control": "public, max-age=300, stale-while-revalidate=120",
        },
      });
      const fetchDuration = Math.round(performance.now() - apiStartTime);
      setStatsFetchTimeMs(fetchDuration);

      console.info(
        `[LandingPage] landingPageStats fetched in ${fetchDuration} ms`,
        {
          url: `${BASE_URL}/landingPageStats`,
          status: response.status,
          headers: response.headers,
          payloadKeys: response?.data ? Object.keys(response.data) : [],
        },
      );

      const fetchedStats = response?.data?.data?.stats || {};
      setStats(fetchedStats);
      writeLandingCache(LANDING_STATS_CACHE_KEY, fetchedStats);
    } catch (err) {
      console.error(
        "[LandingPage] Error occurred while fetching landingPageStats",
        err,
      );
      message.error("Error occurred while fetching the landing page stats");
    } finally {
      setLoadingStats(false);
    }
  }

  return (
    <div>
      <Spinner loading={loadingStats} />
      <div
        onClick={handleSecretClick}
        style={{ display: "block", width: "100%" }}
      >
        <AppHeader />
      </div>
      {metricsUnlocked &&
        (statsFetchTimeMs !== null || topReadyTimeMs !== null) && (
          <div
            style={{
              position: "fixed",
              bottom: 12,
              right: 12,
              zIndex: 1000,
              background: "rgba(255,255,255,0.95)",
              color: "#111",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 10,
              padding: "8px 10px",
              fontSize: 12,
              boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
              maxWidth: 240,
              lineHeight: 1.4,
            }}
          >
            {statsFetchTimeMs !== null && (
              <>
                Stats API: {statsFetchTimeMs} ms
                <br />
              </>
            )}
            {topReadyTimeMs !== null && (
              <>
                Top-screen ready: {topReadyTimeMs} ms{" "}
                {topReadyTimeMs <= 2000 ? "(OK)" : "(Target: under 2000 ms)"}
                <br />
              </>
            )}
          </div>
        )}
      <div>
        <CTASection />
        <BJSPresence data={stats} />
        <BJSMapSection data={stats} />
        <div ref={flipBookRef} data-section="flipBook">
          <Suspense
            fallback={
              <div style={{ padding: 16 }}>Loading flipbook section…</div>
            }
          >
            {visibleSections.flipBook ? (
              <FlipBookSection />
            ) : (
              <div style={{ padding: 16, textAlign: "center" }}>
                Flipbook will load on scroll.
              </div>
            )}
          </Suspense>
        </div>

        <div ref={networkCarouselRef} data-section="networkCarousel">
          <Suspense
            fallback={
              <div style={{ padding: 16 }}>Loading network carousel…</div>
            }
          >
            {visibleSections.networkCarousel ? (
              <NetworkCarousel />
            ) : (
              <div style={{ padding: 16, textAlign: "center" }}>
                Network carousel will load on scroll.
              </div>
            )}
          </Suspense>
        </div>

        <div ref={programsRef} data-section="programs">
          <Suspense
            fallback={
              <div style={{ padding: 16 }}>Loading programs section…</div>
            }
          >
            {visibleSections.programs ? (
              programs.length > 0 ? (
                <ProgramsSection data={programs} />
              ) : loadingPrograms ? (
                <div style={{ padding: 16, textAlign: "center" }}>
                  Loading prominent programs…
                </div>
              ) : (
                <div style={{ padding: 16, textAlign: "center" }}>
                  Prominent programs will load when visible.
                </div>
              )
            ) : (
              <div style={{ padding: 16, textAlign: "center" }}>
                Prominent programs are deferred until scroll.
              </div>
            )}
          </Suspense>
        </div>

        <div ref={impactRef} data-section="impact">
          <Suspense
            fallback={
              <div style={{ padding: 16 }}>Loading impact section…</div>
            }
          >
            {visibleSections.impact ? (
              <ImpactSection />
            ) : (
              <div style={{ padding: 16, textAlign: "center" }}>
                Impact section will load on scroll.
              </div>
            )}
          </Suspense>
        </div>

        <div ref={founderRef} data-section="founder">
          <Suspense
            fallback={
              <div style={{ padding: 16 }}>Loading founder section…</div>
            }
          >
            {visibleSections.founder ? (
              <FromTheFounderSection />
            ) : (
              <div style={{ padding: 16, textAlign: "center" }}>
                Founder section will load on scroll.
              </div>
            )}
          </Suspense>
        </div>

        <div ref={karyaKartaRef} data-section="karyaKarta">
          <Suspense
            fallback={
              <div style={{ padding: 16 }}>Loading karya karta section…</div>
            }
          >
            {visibleSections.karyaKarta ? (
              <KaryaKartaSection />
            ) : (
              <div style={{ padding: 16, textAlign: "center" }}>
                Karya Karta will load on scroll.
              </div>
            )}
          </Suspense>
        </div>
      </div>
      <FooterForLandingPage />
      <ChatbotWidget />
    </div>
  );
};

export default LandingPage;
